import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";

import { PermissionDecisionContext } from "./WriteOnly";

/**
 * One entitlement decision, three renders (UX v0.3 §7).
 *
 * `undefined` is deliberately NOT accepted as "probably fine": an unresolved
 * decision is `none`. The whole system fails closed (I-1), and a component
 * that renders edit controls while the answer is still in flight is exactly
 * the fail-open this invariant exists to prevent.
 */
export type PermissionDecision = "edit" | "view" | "none";

interface PermissionGateProps {
  decision: PermissionDecision | null | undefined;
  /** Named in the copy, so the sentence tells the person WHERE they are limited. */
  areaName: string;
  /**
   * The area owner. `null` when no owner is recorded — the copy then names
   * Damien rather than inventing an owner, because an owner is never inferred
   * (`areas.yaml` leaves `owner_email` null on purpose).
   */
  ownerName?: string | null;
  /**
   * Pass the available height through to the children instead of sizing to
   * them. Set for a DOCKED surface, whose frame fills the viewport — the two
   * read-only wrappers below would otherwise collapse to their content height
   * and break the chain a `h-full` iframe resolves against.
   */
  fill?: boolean;
  children: ReactNode;
}

/** The one sentence, so the badge and the API refusal say exactly the same thing. */
export function readOnlyCopy(areaName: string, ownerName?: string | null): string {
  return ownerName
    ? `View only in ${areaName} — ${ownerName} can give you edit rights`
    : `View only in ${areaName} — ask Damien for edit rights`;
}

/**
 * Shell-owned permission rendering (I-7, BR-13). A module never writes its own
 * permission logic — and, separately, never RELIES on this: the module's API
 * enforces the same decision, because the UI can be bypassed entirely (H-3).
 * This component makes the truth visible; it does not make it true.
 */
export function PermissionGate({
  decision,
  areaName,
  ownerName = null,
  fill = false,
  children,
}: PermissionGateProps) {
  // Deny by default. An unknown decision renders nothing rather than content
  // we have not been told the person may see.
  if (decision !== "edit" && decision !== "view") return null;

  if (decision === "edit") {
    return (
      <PermissionDecisionContext.Provider value="edit">{children}</PermissionDecisionContext.Provider>
    );
  }

  return (
    <PermissionDecisionContext.Provider value="view">
      <div data-permission="view" className={fill ? "flex min-h-0 flex-1 flex-col" : undefined}>
        {/* Every element that can be TYPED INTO or pressed is listed. `textarea`
            was missing, so PROTEX's "What did you check this against?" box stayed
            live under a read-only decision while the button beside it was inert —
            a form the reader can fill in but never submit, and one element short
            of the lockout being about editing at all.

            Known limit, stated rather than implied by the word "inert" below:
            `pointer-events: none` stops the mouse, not the keyboard. A control
            reached by Tab can still be activated by Enter. That is why the API
            enforces the same decision independently (H-3) — this renderer makes
            the truth visible, it is not the thing that makes it true.

            Second known limit, and a DECLARED deviation rather than an oversight
            (PROTEX C3 spec, D-1): designs that call for read-only controls to be
            ABSENT do not get that here — they render, greyed and inert. Hiding
            them instead is one word of CSS and would be wrong until every
            consumer has marked its non-mutating controls: `data-nonmutating` is
            opt-in, so an unmarked filter or picker would VANISH for a viewer
            rather than merely resist a click, and read-only would become
            no-access — which this system does not do (UX S5). The audit comes
            first; changing this selector before it lands would trade a stated
            limit for a silent one. */}
        {/* Controls are visibly non-interactive AND actually inert — a disabled
            look over a live button is a lie the first click exposes.
            `data-nonmutating` is the one exemption: a control that CHANGES
            NOTHING is not an edit control, and locking it out is what turns
            read-only into no-access, which this system does not do (UX S5).
            Export and copy carry it; so does any navigation or selection
            affordance — opening a record to READ it is not an edit. Marking a
            control is a statement about the control, not a permission branch:
            modules never read `decision` to decide what to render.

            A block that ONLY writes (PROTEX's Inbox decision form, GATE-476)
            goes in `<WriteOnly>`, which reads this gate's decision from
            context and is NOT RENDERED for a viewer: absent, not inert, so
            no input, effect or tab stop exists. `<ViewOnly>` renders what
            the viewer reads in its place. Both are opt-in, so nothing an
            author did not mark can vanish (bug 3f80402f-8134). */}
        <div
          aria-readonly="true"
          className={`[&_button:not([data-nonmutating])]:pointer-events-none [&_input:not([data-nonmutating])]:pointer-events-none [&_select:not([data-nonmutating])]:pointer-events-none [&_textarea:not([data-nonmutating])]:pointer-events-none${fill ? " flex min-h-0 flex-1 flex-col" : ""}`}
        >
          {/* The badge sits in the module header, which a screen-reader user
              inside the content region has already passed. Repeating the same
              sentence here means the reason the controls are inert is available
              where the inert controls are — one sentence, both places. */}
          <p className="sr-only">{readOnlyCopy(areaName, ownerName)}</p>
          {children}
        </div>
      </div>
    </PermissionDecisionContext.Provider>
  );
}

/**
 * The read-only badge, rendered in the module header rather than beside every
 * control — per MODULE, not per area, so the truth is stated where the person
 * actually is (UX S5 partial-rights edge).
 */
export function PermissionBadge({
  decision,
  areaName,
  ownerName = null,
}: Omit<PermissionGateProps, "children">) {
  // Full edit shows no badge: chrome that says "everything is normal" is noise
  // on the daily path (UX S4 ideal — badge only when NOT full-edit).
  if (decision !== "view") return null;
  return (
    <Badge variant="outline" className="h-auto whitespace-normal py-1">
      {readOnlyCopy(areaName, ownerName)}
    </Badge>
  );
}
